import { ImageType } from "@/components/ui/tree/tree-context-menu/actions/upload-action";
import { selectMutationForNewRoomSection } from "@/modes/sheet-mode/edit-waypoints/edit-waypoints-store";
import { AppDispatch, AppStore } from "@/store/store";
import { selectCurrentUser } from "@/store/user-selectors";
import { selectRootId } from "@faro-lotv/app-component-toolbox";
import { assert, generateGUID, GUID } from "@faro-lotv/foundation";
import {
  Mutation,
  MutationAddRoomOnSheet,
  ProjectApi,
} from "@faro-lotv/service-wires";
import { Vector3 } from "three";
import { updateProject } from "../project-provider/update-project";
import {
  createMutationAdd2dAttachmentFromFile,
  createMutationAddImg360ToRoomFromFile,
} from "./image-data-upload-utils";
import { groupImageFilesByHeuristic } from "./image-type-heuristic";
import {
  CommonUpdateProjectMultiFilesParams,
  UploadElementType,
} from "./use-upload-element-types";

export type UpdateProjectWithNewWaypointAndImageDataParams = {
  type: UploadElementType.waypointWithImages;

  /** The image type to upload */
  imgType: ImageType;

  /** The name of the waypoint to create. */
  name: string;

  /** The world position of the new room to create */
  newRoomWorldPos: Vector3;
};

/**
 * Update project with uploaded 360 image or 2D image as a new waypoint
 *
 * @param updateProjectParam parameters to be used to update the project
 * @returns Promise that completes when the project has been updated
 */
export function updateProjectWithNewWaypointAndImageData(
  updateProjectParam: CommonUpdateProjectMultiFilesParams &
    UpdateProjectWithNewWaypointAndImageDataParams,
): Promise<void> {
  switch (updateProjectParam.imgType) {
    case ImageType.auto:
      return createNewWaypointWithPhotos(updateProjectParam);
    case ImageType.Img360:
      return createNewTimeSeriesWith360Imgs(updateProjectParam);
    case ImageType.Img2D:
      return createNewLocationWith2DPhotos(updateProjectParam);
  }
}

async function createNewWaypointWithPhotos({
  appStore,
  dispatch,
  projectApi,
  files,
  uploadedFilesData,
  areaId,
  newRoomWorldPos,
  name,
  additionalUrls,
}: CommonUpdateProjectMultiFilesParams &
  UpdateProjectWithNewWaypointAndImageDataParams): Promise<void> {
  assert(
    files.length && files.length === uploadedFilesData.length,
    "Files should be at least one, and as many as the uploaded files data.",
  );

  const {
    createNewRoomSectionMutation,
    roomSectionId,
    rootId,
    userId,
    attachmentGroupId,
  } = createSharedDataForNewRoomSection(
    appStore,
    areaId,
    newRoomWorldPos,
    name,
  );

  const { img360s, img2ds } = await groupImageFilesByHeuristic(
    files,
    uploadedFilesData,
    additionalUrls,
  );

  // Apply mutation: add both the new section and its image as children to the time series
  await applyMutationsForNewLocation(dispatch, projectApi, areaId, [
    createNewRoomSectionMutation,

    ...(await Promise.all([
      ...img360s.map(
        async ({ file, uploadedFileData, thumbnailUri }, index) =>
          await createMutationAddImg360ToRoomFromFile({
            file,
            downloadUrl: uploadedFileData.downloadUrl,
            md5Hash: uploadedFileData.md5,
            thumbnailUri,
            projectApi,
            rootId,
            userId,
            timeSeriesId: createNewRoomSectionMutation.timeSeries.id,
            isFirstPanoInRoom: index === 0,
          }),
      ),
      ...img2ds.map(
        async ({ file, uploadedFileData, thumbnailUri }, index) =>
          await createMutationAdd2dAttachmentFromFile({
            file,
            uploadedFileData,
            thumbnailUri,
            rootId,
            userId,
            roomSectionId,
            shouldCreateAttachmentsGroup: index === 0,
            attachmentGroupId,
          }),
      ),
    ])),
  ]);
}

async function createNewTimeSeriesWith360Imgs({
  appStore,
  dispatch,
  projectApi,
  files,
  uploadedFilesData,
  additionalUrls,
  areaId,
  newRoomWorldPos,
  name,
}: CommonUpdateProjectMultiFilesParams &
  UpdateProjectWithNewWaypointAndImageDataParams): Promise<void> {
  assert(
    files.length && files.length === uploadedFilesData.length,
    "Files should be at least one, and as many as the uploaded files data.",
  );

  const { createNewRoomSectionMutation, rootId, userId } =
    createSharedDataForNewRoomSection(appStore, areaId, newRoomWorldPos, name);

  await applyMutationsForNewLocation(dispatch, projectApi, areaId, [
    createNewRoomSectionMutation,

    ...(await Promise.all(
      files.map(
        async (file, index) =>
          await createMutationAddImg360ToRoomFromFile({
            file,
            downloadUrl: uploadedFilesData[index].downloadUrl,
            thumbnailUri: additionalUrls[index],
            md5Hash: uploadedFilesData[index].md5,
            projectApi,
            rootId,
            userId,
            timeSeriesId: createNewRoomSectionMutation.timeSeries.id,
            isFirstPanoInRoom: index === 0,
          }),
      ),
    )),
  ]);
}

/**
 * Inserts new 2D photo attachments into a new room location.
 */
async function createNewLocationWith2DPhotos({
  appStore,
  dispatch,
  projectApi,
  files,
  uploadedFilesData,
  areaId,
  name,
  additionalUrls,
  newRoomWorldPos,
}: CommonUpdateProjectMultiFilesParams &
  UpdateProjectWithNewWaypointAndImageDataParams): Promise<void> {
  const {
    createNewRoomSectionMutation,
    rootId,
    userId,
    roomSectionId,
    attachmentGroupId,
  } = createSharedDataForNewRoomSection(
    appStore,
    areaId,
    newRoomWorldPos,
    name,
  );

  await applyMutationsForNewLocation(dispatch, projectApi, areaId, [
    createNewRoomSectionMutation,

    ...(await Promise.all(
      files.map(
        async (file, index) =>
          await createMutationAdd2dAttachmentFromFile({
            file,
            uploadedFileData: uploadedFilesData[index],
            thumbnailUri: additionalUrls[index],
            rootId,
            userId,
            roomSectionId,
            shouldCreateAttachmentsGroup: index === 0,
            attachmentGroupId,
          }),
      ),
    )),
  ]);
}

type CreateNewRoomSectionSharedData = {
  /** The ID of the newly created room section */
  roomSectionId: GUID;
  /** The mutation to create the new room section */
  createNewRoomSectionMutation: MutationAddRoomOnSheet;
  /** The root ID of the project, needed to create mutations for the new room section's children */
  rootId: GUID;
  /** The user ID of the current user, needed to create mutations for the new room section's children */
  userId: GUID;
  /** A newly generated ID for a potentially to be used for creating a new attachments group */
  attachmentGroupId: GUID;
};

/**
 * @returns the shared data needed for creating a new room section with image data
 * @param appStore current redux store
 * @param areaId the area ID under which the new room section will be created
 * @param newRoomWorldPos the world position of the new room section
 * @param name the name of the new room section
 */
function createSharedDataForNewRoomSection(
  appStore: AppStore,
  areaId: GUID | undefined,
  newRoomWorldPos: Vector3,
  name: string,
): CreateNewRoomSectionSharedData {
  // Get root ID and user ID from global store
  const rootId = selectRootId(appStore.getState());
  const userId = selectCurrentUser(appStore.getState())?.id;

  assert(rootId, "root ID is not specified");
  assert(userId, "user ID is not specified");
  assert(areaId, "areaId must be provided for creating new room section");

  const roomSectionId = generateGUID();
  const createNewRoomSectionMutation = selectMutationForNewRoomSection({
    areaId,
    id: roomSectionId,
    name,
    worldPos: newRoomWorldPos,
  })(appStore.getState());

  const attachmentGroupId = generateGUID();

  return {
    rootId,
    userId,
    roomSectionId,
    createNewRoomSectionMutation,
    attachmentGroupId,
  };
}

/**
 * Applies mutations and refreshes the project for a new location with uploaded image data.
 *
 * @param dispatch store dispatch function
 * @param projectApi project api instance to apply mutations with
 * @param areaId the current area ID under which the new location is being created, used to refresh the project after mutation application
 * @param mutations the mutations to apply
 */
async function applyMutationsForNewLocation(
  dispatch: AppDispatch,
  projectApi: ProjectApi,
  areaId: GUID | undefined,
  mutations: Mutation[],
): Promise<void> {
  assert(areaId, "areaId must be provided for creating new room section");

  await projectApi.applyMutations(mutations);

  /**
   * Refresh the local project store:
   * Re-fetch all IElements under the original section to reflect the newly added pano.
   */
  await dispatch(
    updateProject({
      projectApi,
      iElementQuery: {
        ancestorIds: [areaId],
      },
    }),
  );
}
